import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoginForm from './LoginForm';
import { motion } from 'framer-motion';
import {
  ChevronRight,
  ChevronLeft,
  X,
  Sparkles,
  Users,
  ShieldCheck,
  Quote,
  CheckCircle2,
  Download,
} from 'lucide-react';
import { getAllEvents, getFeaturedEvents } from '../services/eventService';
import { Event } from '../types';
import ProfileBuilderNew from './ProfileBuilderNew';
import SubscriptionModal from './SubscriptionModal';
import IOSInstallModal from './IOSInstallModal';
import { filterLandingPageEvents } from '../utils/eventFilter';
import { trackEvent, trackPageView } from '../services/analyticsService';
import { isRunningAsApp } from '../utils/appMode';
import { initPWAInstall, canInstallPWA, isIOS, showInstallPrompt, onInstallAvailableChange } from '../utils/pwaInstall';

// Hero images
import heroDesktop from '../assets/hero/cheers-desktop.jpg';
import heroMobile from '../assets/hero/cheers-mobile.jpg';
import heroLogo from '../assets/hero/logo.png';

const GRADIENT = 'linear-gradient(135deg,#FF3148 0%,#00D1FF 100%)';

const LandingPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [showLoginForm, setShowLoginForm] = useState(false);
  const [featuredEvents, setFeaturedEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [formMode, setFormMode] = useState<'login' | 'register'>('login');
  const [showProfileBuilder, setShowProfileBuilder] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [fromVybe, setFromVybe] = useState(false);
  const [showIOSInstallModal, setShowIOSInstallModal] = useState(false);
  const [canInstall, setCanInstall] = useState(canInstallPWA());

  // refs for featured strip
  const stripRef = useRef<HTMLDivElement | null>(null);
  const autoTimerRef = useRef<number | null>(null);

  // Check if running as app (hide install prompts if yes)
  const runningAsApp = isRunningAsApp();

  // TEMPORARY: Redirect all landing page traffic to /vybes
  useEffect(() => {
    navigate('/vybes');
  }, [navigate]);

  // Track page view & check for fromVybe context
  useEffect(() => {
    trackPageView('landing');
    // Initialize PWA install listener
    initPWAInstall();

    // Subscribe to install availability changes
    const unsubscribe = onInstallAvailableChange(() => {
      setCanInstall(canInstallPWA());
    });

    // Check URL params for signup and fromVybe context
    const params = new URLSearchParams(window.location.search);
    const signupParam = params.get('signup');
    const fromVybeParam = params.get('fromVybe');

    if (signupParam === 'true' || signupParam === '1') {
      setShowProfileBuilder(true);
    }
    if (fromVybeParam === 'true') {
      setFromVybe(true);
    }

    return unsubscribe;
  }, []);

  // Load featured events
  useEffect(() => {
    const loadEvents = async () => {
      const allEvents = await getAllEvents();
      const activeEvents = filterLandingPageEvents(allEvents);
      const shuffled = [...activeEvents].sort(() => Math.random() - 0.5);
      setFeaturedEvents(shuffled.slice(0, 12));
    };
    loadEvents();
  }, []);

  useEffect(() => {
    if (isAuthenticated) navigate('/vybes');
  }, [isAuthenticated, navigate]);

  const handleLogin = () => {
    setFormMode('login');
    setShowLoginForm(true);
  };

  const handleSignup = (plan: 'free' | 'premium' = 'free') => {
    trackEvent('signup_button_clicked', { source: 'landing_page', plan });
    if (plan === 'premium') setShowSubscriptionModal(true);
    else setShowProfileBuilder(true);
  };

  const handleInstallApp = async () => {
    trackEvent('install_app_clicked', { source: 'landing_page' });

    // iOS: Show instructions
    if (isIOS()) {
      setShowIOSInstallModal(true);
      return;
    }

    // Chrome/Android: Show native prompt
    const installed = await showInstallPrompt();
    if (installed) {
      trackEvent('app_installed', { source: 'landing_page' });
    }
  };

  const handleCloseAll = () => {
    setShowLoginForm(false);
    setShowProfileBuilder(false);
    setShowSubscriptionModal(false);
    setSelectedEvent(null);
    setShowIOSInstallModal(false);
  };

  const getEventImage = (ev?: any): string => {
    if (!ev) return '';
    const candidates = [
      ev.image_url,
      ev.imageUrl,
      ev.cover_image,
      ev.coverImage,
      ev.banner_url,
      ev.bannerUrl,
      ev.photo_url,
      ev.photoUrl,
      ev.images && Array.isArray(ev.images) && ev.images.length > 0 ? ev.images[0].url || ev.images[0] : '',
    ];
    const pick = candidates.find((c) => typeof c === 'string' && c?.trim().length > 0) as string | undefined;
    return pick || '';
  };

  const getDescription = (ev?: any) => {
    const raw =
      ev?.description ||
      ev?.details ||
      ev?.summary ||
      (Array.isArray(ev?.tags) ? ev.tags.join(' • ') : '') ||
      '';
    const safe = typeof raw === 'string' ? raw.trim() : '';
    if (!safe) return '';
    return safe.length > 92 ? `${safe.slice(0, 90)}…` : safe;
  };

  // Auto-scroll featured strip on mobile
  useEffect(() => {
    const rail = stripRef.current;
    if (!rail) return;

    const intervalMs = 3600;
    const tick = () => {
      const cardW = 270;
      const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      if (atEnd) rail.scrollTo({ left: 0, behavior: 'smooth' });
      else rail.scrollBy({ left: cardW, behavior: 'smooth' });
    };

    autoTimerRef.current = window.setInterval(tick, intervalMs);
    return () => {
      if (autoTimerRef.current) window.clearInterval(autoTimerRef.current);
    };
  }, [featuredEvents]);

  const manualScroll = (dir: 'left' | 'right') => {
    const rail = stripRef.current;
    if (!rail) return;
    if (autoTimerRef.current) {
      window.clearInterval(autoTimerRef.current);
      autoTimerRef.current = null;
    }
    rail.scrollBy({ left: dir === 'right' ? 290 : -290, behavior: 'smooth' });
  };

  // --- On-brand building blocks (consistent borders & cards) ---
  const GradientRing: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
    <div className={`relative rounded-2xl ${className}`} style={{ padding: '2px' }}>
      <div
        className="absolute inset-0 rounded-2xl"
        style={{
          background: GRADIENT,
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
          padding: '2px',
          opacity: 0.95,
        }}
      />
      <div className="relative rounded-2xl bg-white/6 border border-white/10 backdrop-blur-md shadow-elegant">
        {children}
      </div>
    </div>
  );

  const PrimaryButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ className = '', ...props }) => (
    <button
      {...props}
      className={`w-full sm:w-auto inline-flex items-center justify-center px-10 py-3.5 text-white text-lg font-semibold rounded-full
      bg-gradient-to-r from-[#ff3148] to-[#ff2aa2] hover:from-[#ff2a3f] hover:to-[#ff1f95]
      shadow-lg shadow-[#ff3148]/20 hover:shadow-xl hover:shadow-[#ff3148]/25
      focus:outline-none focus:ring-4 focus:ring-[#ff3148]/35 transition-all active:scale-[.99] ${className}`}
    />
  );

  const SecondaryButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ className = '', ...props }) => (
    <button
      {...props}
      className={`w-full sm:w-auto inline-flex items-center justify-center px-10 py-3.5 text-white text-lg font-semibold rounded-full
      bg-white/8 hover:bg-white/12 border border-white/18 hover:border-white/28
      focus:outline-none focus:ring-2 focus:ring-white/35 transition-all active:scale-[.99] ${className}`}
    />
  );

  const LinkButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ className = '', ...props }) => (
    <button
      {...props}
      className={`text-white/90 hover:text-white text-sm md:text-base font-medium underline underline-offset-2
      focus:outline-none focus:ring-2 focus:ring-white/35 rounded-md px-4 py-2 transition-all active:scale-[.99] ${className}`}
    />
  );

  const testimonialCards = useMemo(
    () => [
      {
        label: 'Early access member',
        text: 'Feels natural — you join a plan and see what happens, without the awkwardness.',
      },
      {
        label: 'Early access member',
        text: 'Not dating, not endless chat. Just people doing things.',
      },
      {
        label: 'Early access member',
        text: '“Join or leave freely” makes it feel safe to try.',
      },
    ],
    []
  );

  // ---------- Modals ----------
  if (showLoginForm || selectedEvent) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      >
        <div className="relative w-full max-w-md max-h-[90vh] bg-theme-cards border border-theme-cards rounded-2xl shadow-card overflow-visible p-6">
          <button
            type="button"
            onClick={handleCloseAll}
            className="absolute top-3 right-3 inline-flex items-center justify-center h-8 w-8 rounded-full border border-secondary/40 text-secondary hover:bg-secondary/10 focus:outline-none focus:ring-2 focus:ring-primary transition-all"
            aria-label="Close modal"
          >
            <X size={16} />
          </button>
          <LoginForm
            initialMode={formMode}
            onModeChange={setFormMode}
            onSignupClick={() => {
              setShowLoginForm(false);
              setShowProfileBuilder(true);
            }}
          />
        </div>
      </motion.div>
    );
  }

  if (showProfileBuilder) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
      >
        <div className="relative w-full max-w-md h-[90vh] max-h-[800px]">
          <button
            type="button"
            onClick={handleCloseAll}
            className="absolute -top-2 -right-2 inline-flex items-center justify-center h-10 w-10 rounded-full bg-theme-cards border border-secondary/40 text-secondary hover:bg-secondary/10 focus:outline-none z-50 shadow-lg focus:ring-2 focus:ring-primary transition-all"
            aria-label="Close profile builder"
          >
            <X size={20} />
          </button>
          <div className="w-full h-full rounded-2xl overflow-hidden shadow-2xl">
            <ProfileBuilderNew
              onComplete={() => setShowProfileBuilder(false)}
              onLoginClick={() => {
                setShowProfileBuilder(false);
                setFormMode('login');
                setShowLoginForm(true);
              }}
              fromVybe={fromVybe}
            />
          </div>
        </div>
      </motion.div>
    );
  }

  if (showSubscriptionModal) {
    return (
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={handleCloseAll}
        requiredFeature="Premium Membership"
      />
    );
  }

  return (
    <>
      {/* iOS Install Modal */}
      <IOSInstallModal
        isOpen={showIOSInstallModal}
        onClose={() => setShowIOSInstallModal(false)}
      />

      <div className="min-h-screen bg-[#0A0E27] text-white">
      {/* HERO */}
      <section className="relative min-h-[720px] md:min-h-[760px] flex items-center justify-center overflow-hidden">
        <picture>
          <source media="(max-width: 768px)" srcSet={heroMobile} />
          <img
            src={heroDesktop}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center z-0"
            loading="eager"
            fetchpriority="high"
          />
        </picture>

        {/* Consistent on-brand overlay */}
        <div className="absolute inset-0 z-10 bg-gradient-to-br from-black/75 via-black/55 to-black/75" />

        {/* Subtle brand glow */}
        <div
          className="absolute -top-24 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-3xl opacity-20 z-10"
          style={{ background: GRADIENT }}
        />

        <div className="relative z-20 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.img
            src={heroLogo}
            alt="Vybe Tribe"
            className="mx-auto mb-6 w-72 md:w-96 h-auto"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          />

          {/* Trust layer (consistent card style) */}
          <motion.div
            className="mx-auto mb-7 max-w-2xl"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <GradientRing className="text-left">
              <div className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-white/10 border border-white/10">
                    <CheckCircle2 className="w-5 h-5 text-white/90" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-white text-sm md:text-base">
                      Built for real people with real plans
                    </h3>
                    <div className="mt-2 space-y-1.5 text-white/85 text-xs md:text-sm">
                      <div className="flex gap-2">
                        <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-white/50" />
                        <span>Free to sign up — not dating</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-white/50" />
                        <span>Real plans — not endless chatting</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-white/50" />
                        <span>Join or leave any Vybe freely</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </GradientRing>
          </motion.div>

          {/* CTAs (consistent styles) */}
          <motion.div
            className="flex flex-col items-center justify-center"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <PrimaryButton onClick={() => handleSignup('free')} aria-label="Sign up free">
              Sign up free
            </PrimaryButton>

            {/* Get the app button - only show in browser, not in app */}
            {!runningAsApp && canInstall && (
              <div className="mt-3 p-[2px] rounded-full bg-gradient-to-r from-[#FF3148] to-[#00D1FF]">
                <button
                  onClick={handleInstallApp}
                  className="flex items-center gap-2 text-white/80 hover:text-white px-6 py-2.5 rounded-full bg-theme-background transition-all font-medium text-sm w-full"
                  aria-label="Get the app"
                >
                  <Download size={16} />
                  Get the app (recommended)
                </button>
              </div>
            )}

            <SecondaryButton onClick={() => navigate('/vybes')} aria-label="Explore Vybes" className="mt-4">
              Explore Vybes
            </SecondaryButton>

            <LinkButton onClick={handleLogin} aria-label="Log in to your account" className="mt-3">
              Log in
            </LinkButton>
          </motion.div>
        </div>
      </section>

      {/* FEATURED (mobile) – consistent card styles */}
      <section className="px-5 pt-6 pb-10 md:hidden relative">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Featured Vybes</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => manualScroll('left')}
                className="h-9 w-9 rounded-full bg-white/8 border border-white/15 grid place-items-center active:scale-95"
                aria-label="Scroll left"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => manualScroll('right')}
                className="h-9 w-9 rounded-full bg-white/8 border border-white/15 grid place-items-center active:scale-95"
                aria-label="Scroll right"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          <div ref={stripRef} className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory">
            {(featuredEvents.length ? featuredEvents : Array.from({ length: 8 })).slice(0, 10).map((raw, i) => {
              const ev = (raw as Event) || ({} as Event);
              const img =
                getEventImage(ev) ||
                'https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&w=800&q=60';
              const title =
                (ev as any)?.title?.toString().trim() ||
                ['Tonight plan', 'Nearby vybe', 'Foodie thing', 'Out late', 'Coffee run', 'Morning hike', 'Art sesh', 'Game night'][i % 8];
              const desc = getDescription(ev) || 'Tap to see the plan.';

              return (
                <button
                  key={(ev as any)?.id || `skeleton-${i}`}
                  onClick={() =>
                    navigate((ev as any)?.id ? `/vybes?highlight=${encodeURIComponent((ev as any).id)}` : '/vybes')
                  }
                  className="shrink-0 w-[70vw] max-w-[290px] snap-start active:scale-[.99] text-left"
                  aria-label={`Open ${title}`}
                >
                  <GradientRing>
                    <div className="relative h-48 w-full overflow-hidden rounded-2xl">
                      <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <div className="text-white font-semibold leading-tight line-clamp-1">{title}</div>
                        <div className="text-white/80 text-[11px] leading-snug line-clamp-2 mt-1">{desc}</div>
                      </div>
                    </div>
                  </GradientRing>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* VALUE PROPS – keep it consistent */}
      <section className="py-12 md:py-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold">
              Catch your <span className="text-[#FF3148]">Vybe</span>. Build your <span className="text-[#00D1FF]">Tribe</span>.
            </h2>
            <p className="text-white/70 mt-2 max-w-2xl mx-auto">
              Find plans near you, meet people naturally, and grow a social circle through shared interests.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <GradientRing>
              <div className="p-5 md:p-6">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 grid place-items-center w-12 h-12 rounded-xl bg-white/10 border border-white/10">
                    <Sparkles className="w-7 h-7 text-white/90" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Pick a Vybe</h3>
                    <p className="text-white/75 text-sm mt-1 leading-relaxed">
                      Join community Vybes created by the Vybe Tribe team, or host your own — coffee catchups, gigs, walks, food, sport.
                    </p>
                  </div>
                </div>
              </div>
            </GradientRing>

            <GradientRing>
              <div className="p-5 md:p-6">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 grid place-items-center w-12 h-12 rounded-xl bg-white/10 border border-white/10">
                    <Users className="w-7 h-7 text-white/90" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Meet your people</h3>
                    <p className="text-white/75 text-sm mt-1 leading-relaxed">
                      Build your Tribe through doing — not swiping, not awkward intros.
                    </p>
                  </div>
                </div>
              </div>
            </GradientRing>

            <GradientRing>
              <div className="p-5 md:p-6">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 grid place-items-center w-12 h-12 rounded-xl bg-white/10 border border-white/10">
                    <ShieldCheck className="w-7 h-7 text-white/90" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Safer by design</h3>
                    <p className="text-white/75 text-sm mt-1 leading-relaxed">
                      Verified Tribe Members and clear control — you decide what happens.
                    </p>
                  </div>
                </div>
              </div>
            </GradientRing>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL PLACEHOLDERS – consistent style */}
      <section className="py-12 md:py-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold">What people are saying</h2>
            <p className="text-white/70 mt-2 max-w-2xl mx-auto">
              Early feedback from people trying Vybe Tribe.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonialCards.map((t, idx) => (
              <GradientRing key={idx}>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-white/10 border border-white/10 grid place-items-center">
                        <Users className="h-5 w-5 text-white/75" />
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-semibold text-white">{t.label}</div>
                        <div className="text-xs text-white/55">Verified Tribe Member</div>
                      </div>
                    </div>
                    <Quote className="h-5 w-5 text-white/30" />
                  </div>

                  <p className="text-white/85 text-sm leading-relaxed">“{t.text}”</p>
                </div>
              </GradientRing>
            ))}
          </div>

          <div className="text-center mt-8">
            <PrimaryButton onClick={() => handleSignup('free')} aria-label="Sign up free" className="text-base px-7 py-3">
              Sign up free
            </PrimaryButton>
            <div className="text-white/60 text-xs mt-2">Nothing happens unless you choose.</div>
          </div>
        </div>
      </section>

      {/* Footer handled by Layout */}
      </div>
    </>
  );
};

export default LandingPage;
